import React, { useState, useEffect, useRef, useCallback } from 'react';
import ProductCard from './ProductCard';

const PAGE_SIZE = 24;

function ProductGrid({ products, selectedProducts, onAddToCart, onRemove, onQuantityChange, loading }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // Reset visible count when products list changes (search filter, refresh)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [products]);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + PAGE_SIZE, products.length));
  }, [products.length]);

  // Re-create the observer when the visible count changes too — otherwise, if the
  // sentinel stays within view after a page loads, the observer won't fire again and
  // infinite scroll stalls (the spinner sits there forever).
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '300px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, visibleCount]);

  // Only take over the whole area with a spinner on the FIRST load (nothing to show yet).
  // On refresh we keep the existing grid visible so the page doesn't blank out.
  if (loading && products.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
        </svg>
        <h3>No products found</h3>
        <p>Try adjusting your search or check back later</p>
      </div>
    );
  }

  const visibleProducts = products.slice(0, visibleCount);
  const hasMore = visibleCount < products.length;

  return (
    <>
      <div className="product-grid">
        {visibleProducts.map(product => (
          <ProductCard
            key={product.id}
            product={product}
            isSelected={!!selectedProducts[product.id]}
            cartQuantity={selectedProducts[product.id]?.quantity || 1}
            selectedCustomization={selectedProducts[product.id]?.customization || 'no'}
            onAddToCart={onAddToCart}
            onRemove={onRemove}
            onQuantityChange={onQuantityChange}
          />
        ))}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="load-more-sentinel">
          <div className="loading-spinner"></div>
        </div>
      )}

      {!hasMore && products.length > PAGE_SIZE && (
        <p className="all-loaded">All {products.length} products loaded</p>
      )}
    </>
  );
}

export default ProductGrid;
